/**
 * Authentication Error Handling Model
 *
 * Provides structured error types, codes, and interfaces for
 * consistent error handling across all authentication features.
 */
import { InjectionToken } from '@angular/core';
import { environment } from '@environments/environment';

/**
 * Authentication error codes - stable identifiers for error handling
 */
export enum AuthErrorCode {
  // Credential errors
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  INVALID_EMAIL = 'INVALID_EMAIL',
  INVALID_PASSWORD = 'INVALID_PASSWORD',
  WEAK_PASSWORD = 'WEAK_PASSWORD',

  // Account state errors
  ACCOUNT_LOCKED = 'ACCOUNT_LOCKED',
  ACCOUNT_DISABLED = 'ACCOUNT_DISABLED',
  ACCOUNT_NOT_FOUND = 'ACCOUNT_NOT_FOUND',
  ACCOUNT_NOT_VERIFIED = 'ACCOUNT_NOT_VERIFIED',
  EMAIL_ALREADY_EXISTS = 'EMAIL_ALREADY_EXISTS',
  USERNAME_ALREADY_EXISTS = 'USERNAME_ALREADY_EXISTS',

  // MFA errors
  MFA_REQUIRED = 'MFA_REQUIRED',
  MFA_INVALID_CODE = 'MFA_INVALID_CODE',
  MFA_CODE_EXPIRED = 'MFA_CODE_EXPIRED',
  MFA_TOO_MANY_ATTEMPTS = 'MFA_TOO_MANY_ATTEMPTS',
  MFA_NOT_CONFIGURED = 'MFA_NOT_CONFIGURED',
  BACKUP_CODE_INVALID = 'BACKUP_CODE_INVALID',

  // Session/Token errors
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  TOKEN_INVALID = 'TOKEN_INVALID',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  REFRESH_TOKEN_EXPIRED = 'REFRESH_TOKEN_EXPIRED',

  // Rate limiting
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  TOO_MANY_ATTEMPTS = 'TOO_MANY_ATTEMPTS',

  // Network/Server errors
  NETWORK_ERROR = 'NETWORK_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  TIMEOUT = 'TIMEOUT',

  // Validation errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_INPUT = 'INVALID_INPUT',

  // Password reset
  RESET_TOKEN_EXPIRED = 'RESET_TOKEN_EXPIRED',
  RESET_TOKEN_INVALID = 'RESET_TOKEN_INVALID',
  PASSWORD_RECENTLY_USED = 'PASSWORD_RECENTLY_USED',

  // Generic
  UNKNOWN = 'UNKNOWN',
  PERMISSION_DENIED = 'PERMISSION_DENIED'
}

/**
 * Error severity levels for UI display and logging
 */
export enum AuthErrorSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical'
}

/**
 * Error categories for grouping and handling
 */
export enum AuthErrorCategory {
  VALIDATION = 'validation',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  RATE_LIMITING = 'rate_limiting',
  NETWORK = 'network',
  SERVER = 'server',
  SESSION = 'session',
  MFA = 'mfa',
  ACCOUNT = 'account'
}

/**
 * Recovery action types
 */
export enum AuthRecoveryAction {
  RETRY = 'retry',
  CONTACT_SUPPORT = 'contact_support',
  RESET_PASSWORD = 'reset_password',
  VERIFY_EMAIL = 'verify_email',
  WAIT_AND_RETRY = 'wait_and_retry',
  CHECK_CONNECTION = 'check_connection',
  LOGOUT = 'logout',
  CONFIGURE_MFA = 'configure_mfa',
  NONE = 'none'
}

/**
 * Structured authentication error
 */
export interface AuthError {
  /** Stable error code for programmatic handling */
  code: AuthErrorCode;

  /** User-friendly error message */
  message: string;

  /** Technical message for logging (may contain sensitive info) */
  technicalMessage?: string;

  /** HTTP status code if applicable */
  statusCode?: number;

  /** Error severity for UI styling */
  severity: AuthErrorSeverity;

  /** Error category for grouping */
  category: AuthErrorCategory;

  /** Suggested recovery action */
  recoveryAction: AuthRecoveryAction;

  /** Additional recovery guidance for user */
  recoveryGuidance?: string;

  /** Rate limit wait time in seconds (if applicable) */
  retryAfterSeconds?: number;

  /** Account unlock time (if applicable) */
  unlockTime?: Date;

  /** Support contact info (if applicable) */
  supportLink?: string;

  /** Original error for debugging */
  originalError?: unknown;

  /** Request ID for support reference */
  requestId?: string;

  /** Timestamp when error occurred */
  timestamp: Date;

  /** Whether this error should be logged */
  shouldLog: boolean;

  /** Whether to show notification */
  showNotification: boolean;
}

/**
 * Error metadata from API responses
 */
export interface ApiErrorResponse {
  error?: string;
  message?: string;
  code?: string;
  statusCode?: number;
  details?: Record<string, unknown>;
  retryAfter?: number;
  unlockAt?: string;
  requestId?: string;
}

/**
 * Configuration for error handling behavior
 */
export interface AuthErrorConfig {
  /** Default retry count for transient errors */
  defaultRetryCount: number;

  /** Default retry delay in ms */
  defaultRetryDelay: number;

  /** Max retry delay (with exponential backoff) */
  maxRetryDelay: number;

  /** Enable automatic retry for network errors */
  autoRetryNetworkErrors: boolean;

  /** Show console errors in development */
  logErrorsToConsole: boolean;

  /** Support email for error messages */
  supportEmail: string;

  /** Support URL for error messages */
  supportUrl: string;

  /** Error-specific wait times in milliseconds */
  waitTimes: AuthErrorWaitTimes;
}

/**
 * Configurable wait times for specific error types
 */
export interface AuthErrorWaitTimes {
  /** Wait time for rate limit exceeded errors (default: 5000ms = 5 seconds) */
  rateLimitExceeded: number;

  /** Wait time for too many login/auth attempts (default: 300000ms = 5 minutes) */
  tooManyAttempts: number;

  /** Wait time for account locked errors (default: 900000ms = 15 minutes) */
  accountLocked: number;

  /** Wait time for MFA too many attempts (default: 300000ms = 5 minutes) */
  mfaTooManyAttempts: number;

  /** Wait time for password reset redirect (default: 3000ms = 3 seconds) */
  passwordResetRedirect: number;

  /** Wait time for session timeout warning before auto-logout (default: 60000ms = 1 minute) */
  sessionTimeoutWarning: number;
}

/**
 * Default wait times for specific error types
 */
export const DEFAULT_AUTH_WAIT_TIMES: AuthErrorWaitTimes = {
  rateLimitExceeded: 5000,      // 5 seconds
  tooManyAttempts: 300000,      // 5 minutes
  accountLocked: 900000,        // 15 minutes
  mfaTooManyAttempts: 300000,   // 5 minutes
  passwordResetRedirect: 3000,  // 3 seconds
  sessionTimeoutWarning: 60000  // 1 minute
};

/**
 * Default error configuration
 */
export const DEFAULT_AUTH_ERROR_CONFIG: AuthErrorConfig = {
  defaultRetryCount: 3,
  defaultRetryDelay: 1000,
  maxRetryDelay: 30000,
  autoRetryNetworkErrors: true,
  logErrorsToConsole: true,
  // Per docs/gaps-fe.md GAP-FE-028: pulled from environment.urls so
  // staging/dev can substitute scoped support inboxes without code edits.
  supportEmail: environment.urls.supportEmail,
  supportUrl: environment.urls.support,
  waitTimes: DEFAULT_AUTH_WAIT_TIMES
};

/**
 * User-friendly error messages for each error code
 */
export const AUTH_ERROR_MESSAGES: Record<AuthErrorCode, string> = {
  [AuthErrorCode.INVALID_CREDENTIALS]: 'Invalid email or password. Please check your credentials and try again.',
  [AuthErrorCode.INVALID_EMAIL]: 'Please enter a valid email address.',
  [AuthErrorCode.INVALID_PASSWORD]: 'Invalid password. Please try again.',
  [AuthErrorCode.WEAK_PASSWORD]: 'Password is too weak. Please use a stronger password with at least 8 characters, including uppercase, lowercase, numbers, and special characters.',

  [AuthErrorCode.ACCOUNT_LOCKED]: 'Your account has been temporarily locked due to multiple failed login attempts. Please try again later or contact support.',
  [AuthErrorCode.ACCOUNT_DISABLED]: 'Your account has been disabled. Please contact support for assistance.',
  [AuthErrorCode.ACCOUNT_NOT_FOUND]: 'Invalid email or password. Please check your credentials and try again.',
  [AuthErrorCode.ACCOUNT_NOT_VERIFIED]: 'Please verify your email address before logging in. Check your inbox for the verification link.',
  [AuthErrorCode.EMAIL_ALREADY_EXISTS]: 'An account with this email already exists. Please try logging in or use a different email.',
  [AuthErrorCode.USERNAME_ALREADY_EXISTS]: 'This username is already taken. Please choose a different username.',

  [AuthErrorCode.MFA_REQUIRED]: 'Multi-factor authentication is required. Please enter your verification code.',
  [AuthErrorCode.MFA_INVALID_CODE]: 'Invalid verification code. Please check the code and try again.',
  [AuthErrorCode.MFA_CODE_EXPIRED]: 'Verification code has expired. Please request a new code.',
  [AuthErrorCode.MFA_TOO_MANY_ATTEMPTS]: 'Too many verification attempts. Please wait a few minutes before trying again.',
  [AuthErrorCode.MFA_NOT_CONFIGURED]: 'Multi-factor authentication is not set up. Please configure MFA in your security settings.',
  [AuthErrorCode.BACKUP_CODE_INVALID]: 'Invalid backup code. Please check the code and try again.',

  [AuthErrorCode.TOKEN_EXPIRED]: 'Your session has expired. Please log in again.',
  [AuthErrorCode.TOKEN_INVALID]: 'Invalid session. Please log in again.',
  [AuthErrorCode.SESSION_EXPIRED]: 'Your session has expired. Please log in again.',
  [AuthErrorCode.REFRESH_TOKEN_EXPIRED]: 'Your session has expired. Please log in again.',

  [AuthErrorCode.RATE_LIMIT_EXCEEDED]: 'Too many requests. Please wait a moment before trying again.',
  [AuthErrorCode.TOO_MANY_ATTEMPTS]: 'Too many failed attempts. Please wait before trying again.',

  [AuthErrorCode.NETWORK_ERROR]: 'Unable to connect. Please check your internet connection and try again.',
  [AuthErrorCode.SERVER_ERROR]: 'Server error. Please try again later. If the problem persists, contact support.',
  [AuthErrorCode.SERVICE_UNAVAILABLE]: 'Service temporarily unavailable. Please try again in a few minutes.',
  [AuthErrorCode.TIMEOUT]: 'Request timed out. Please check your connection and try again.',

  [AuthErrorCode.VALIDATION_ERROR]: 'Please check your input and try again.',
  [AuthErrorCode.INVALID_INPUT]: 'Invalid input. Please check your information and try again.',

  [AuthErrorCode.RESET_TOKEN_EXPIRED]: 'Password reset link has expired. Please request a new one.',
  [AuthErrorCode.RESET_TOKEN_INVALID]: 'Invalid password reset link. Please request a new one.',
  [AuthErrorCode.PASSWORD_RECENTLY_USED]: 'This password was used recently. Please choose a different password.',

  [AuthErrorCode.UNKNOWN]: 'An unexpected error occurred. Please try again or contact support.',
  [AuthErrorCode.PERMISSION_DENIED]: 'You do not have permission to perform this action.'
};

/**
 * Recovery guidance for each error code
 */
export const AUTH_ERROR_RECOVERY_GUIDANCE: Record<AuthErrorCode, string> = {
  [AuthErrorCode.INVALID_CREDENTIALS]: 'Double-check your email and password. If you forgot your password, use the "Forgot Password" link.',
  [AuthErrorCode.INVALID_EMAIL]: 'Enter your email in the format: example@domain.com',
  [AuthErrorCode.INVALID_PASSWORD]: 'Try entering your password again carefully.',
  [AuthErrorCode.WEAK_PASSWORD]: 'Use at least 8 characters with a mix of uppercase, lowercase, numbers, and symbols.',

  [AuthErrorCode.ACCOUNT_LOCKED]: `Wait 15 minutes or contact support at ${environment.urls.supportEmail} to unlock your account.`,
  [AuthErrorCode.ACCOUNT_DISABLED]: `Contact support at ${environment.urls.supportEmail} to reactivate your account.`,
  [AuthErrorCode.ACCOUNT_NOT_FOUND]: 'Double-check your email and password. If you forgot your password, use the "Forgot Password" link.',
  [AuthErrorCode.ACCOUNT_NOT_VERIFIED]: 'Check your inbox and spam folder for the verification email. Need a new link? Contact support.',
  [AuthErrorCode.EMAIL_ALREADY_EXISTS]: 'Use the login page if you already have an account, or try the "Forgot Password" option.',
  [AuthErrorCode.USERNAME_ALREADY_EXISTS]: 'Try adding numbers or underscores to make your username unique.',

  [AuthErrorCode.MFA_REQUIRED]: 'Open your authenticator app and enter the 6-digit code shown.',
  [AuthErrorCode.MFA_INVALID_CODE]: 'Make sure you are entering the latest code from your authenticator app.',
  [AuthErrorCode.MFA_CODE_EXPIRED]: 'Wait for a new code to appear in your authenticator app.',
  [AuthErrorCode.MFA_TOO_MANY_ATTEMPTS]: 'Wait 5 minutes before trying again, or use a backup code.',
  [AuthErrorCode.MFA_NOT_CONFIGURED]: 'Go to Settings > Security to set up two-factor authentication.',
  [AuthErrorCode.BACKUP_CODE_INVALID]: 'Check that you entered all 8 characters correctly. Each backup code can only be used once.',

  [AuthErrorCode.TOKEN_EXPIRED]: 'Log in with your email and password.',
  [AuthErrorCode.TOKEN_INVALID]: 'Clear your browser cache and log in again.',
  [AuthErrorCode.SESSION_EXPIRED]: 'Log in again to continue.',
  [AuthErrorCode.REFRESH_TOKEN_EXPIRED]: 'Log in with your email and password.',

  [AuthErrorCode.RATE_LIMIT_EXCEEDED]: 'Wait a few seconds and try again.',
  [AuthErrorCode.TOO_MANY_ATTEMPTS]: 'Wait 5 minutes before trying again.',

  [AuthErrorCode.NETWORK_ERROR]: 'Check your Wi-Fi or mobile data connection. Try refreshing the page.',
  [AuthErrorCode.SERVER_ERROR]: 'Wait a few minutes and try again. If this continues, contact support.',
  [AuthErrorCode.SERVICE_UNAVAILABLE]: 'The system is undergoing maintenance. Try again in a few minutes.',
  [AuthErrorCode.TIMEOUT]: 'Refresh the page and try again.',

  [AuthErrorCode.VALIDATION_ERROR]: 'Review the highlighted fields and correct any errors.',
  [AuthErrorCode.INVALID_INPUT]: 'Check that all fields are filled out correctly.',

  [AuthErrorCode.RESET_TOKEN_EXPIRED]: 'Request a new password reset link from the login page.',
  [AuthErrorCode.RESET_TOKEN_INVALID]: 'Request a new password reset link from the login page.',
  [AuthErrorCode.PASSWORD_RECENTLY_USED]: 'Choose a password you have not used in the last 6 months.',

  [AuthErrorCode.UNKNOWN]: 'Try again or contact support if the issue persists.',
  [AuthErrorCode.PERMISSION_DENIED]: 'Contact your administrator if you need access to this feature.'
};

/**
 * Mapping of error codes to categories
 */
export const AUTH_ERROR_CATEGORIES: Record<AuthErrorCode, AuthErrorCategory> = {
  [AuthErrorCode.INVALID_CREDENTIALS]: AuthErrorCategory.AUTHENTICATION,
  [AuthErrorCode.INVALID_EMAIL]: AuthErrorCategory.VALIDATION,
  [AuthErrorCode.INVALID_PASSWORD]: AuthErrorCategory.AUTHENTICATION,
  [AuthErrorCode.WEAK_PASSWORD]: AuthErrorCategory.VALIDATION,

  [AuthErrorCode.ACCOUNT_LOCKED]: AuthErrorCategory.ACCOUNT,
  [AuthErrorCode.ACCOUNT_DISABLED]: AuthErrorCategory.ACCOUNT,
  [AuthErrorCode.ACCOUNT_NOT_FOUND]: AuthErrorCategory.ACCOUNT,
  [AuthErrorCode.ACCOUNT_NOT_VERIFIED]: AuthErrorCategory.ACCOUNT,
  [AuthErrorCode.EMAIL_ALREADY_EXISTS]: AuthErrorCategory.VALIDATION,
  [AuthErrorCode.USERNAME_ALREADY_EXISTS]: AuthErrorCategory.VALIDATION,

  [AuthErrorCode.MFA_REQUIRED]: AuthErrorCategory.MFA,
  [AuthErrorCode.MFA_INVALID_CODE]: AuthErrorCategory.MFA,
  [AuthErrorCode.MFA_CODE_EXPIRED]: AuthErrorCategory.MFA,
  [AuthErrorCode.MFA_TOO_MANY_ATTEMPTS]: AuthErrorCategory.MFA,
  [AuthErrorCode.MFA_NOT_CONFIGURED]: AuthErrorCategory.MFA,
  [AuthErrorCode.BACKUP_CODE_INVALID]: AuthErrorCategory.MFA,

  [AuthErrorCode.TOKEN_EXPIRED]: AuthErrorCategory.SESSION,
  [AuthErrorCode.TOKEN_INVALID]: AuthErrorCategory.SESSION,
  [AuthErrorCode.SESSION_EXPIRED]: AuthErrorCategory.SESSION,
  [AuthErrorCode.REFRESH_TOKEN_EXPIRED]: AuthErrorCategory.SESSION,

  [AuthErrorCode.RATE_LIMIT_EXCEEDED]: AuthErrorCategory.RATE_LIMITING,
  [AuthErrorCode.TOO_MANY_ATTEMPTS]: AuthErrorCategory.RATE_LIMITING,

  [AuthErrorCode.NETWORK_ERROR]: AuthErrorCategory.NETWORK,
  [AuthErrorCode.SERVER_ERROR]: AuthErrorCategory.SERVER,
  [AuthErrorCode.SERVICE_UNAVAILABLE]: AuthErrorCategory.SERVER,
  [AuthErrorCode.TIMEOUT]: AuthErrorCategory.NETWORK,

  [AuthErrorCode.VALIDATION_ERROR]: AuthErrorCategory.VALIDATION,
  [AuthErrorCode.INVALID_INPUT]: AuthErrorCategory.VALIDATION,

  [AuthErrorCode.RESET_TOKEN_EXPIRED]: AuthErrorCategory.SESSION,
  [AuthErrorCode.RESET_TOKEN_INVALID]: AuthErrorCategory.SESSION,
  [AuthErrorCode.PASSWORD_RECENTLY_USED]: AuthErrorCategory.VALIDATION,

  [AuthErrorCode.UNKNOWN]: AuthErrorCategory.SERVER,
  [AuthErrorCode.PERMISSION_DENIED]: AuthErrorCategory.AUTHORIZATION
};

/**
 * Mapping of error codes to recovery actions
 */
export const AUTH_ERROR_RECOVERY_ACTIONS: Record<AuthErrorCode, AuthRecoveryAction> = {
  [AuthErrorCode.INVALID_CREDENTIALS]: AuthRecoveryAction.RETRY,
  [AuthErrorCode.INVALID_EMAIL]: AuthRecoveryAction.RETRY,
  [AuthErrorCode.INVALID_PASSWORD]: AuthRecoveryAction.RETRY,
  [AuthErrorCode.WEAK_PASSWORD]: AuthRecoveryAction.RETRY,

  [AuthErrorCode.ACCOUNT_LOCKED]: AuthRecoveryAction.WAIT_AND_RETRY,
  [AuthErrorCode.ACCOUNT_DISABLED]: AuthRecoveryAction.CONTACT_SUPPORT,
  [AuthErrorCode.ACCOUNT_NOT_FOUND]: AuthRecoveryAction.RETRY,
  [AuthErrorCode.ACCOUNT_NOT_VERIFIED]: AuthRecoveryAction.VERIFY_EMAIL,
  [AuthErrorCode.EMAIL_ALREADY_EXISTS]: AuthRecoveryAction.RETRY,
  [AuthErrorCode.USERNAME_ALREADY_EXISTS]: AuthRecoveryAction.RETRY,

  [AuthErrorCode.MFA_REQUIRED]: AuthRecoveryAction.NONE,
  [AuthErrorCode.MFA_INVALID_CODE]: AuthRecoveryAction.RETRY,
  [AuthErrorCode.MFA_CODE_EXPIRED]: AuthRecoveryAction.RETRY,
  [AuthErrorCode.MFA_TOO_MANY_ATTEMPTS]: AuthRecoveryAction.WAIT_AND_RETRY,
  [AuthErrorCode.MFA_NOT_CONFIGURED]: AuthRecoveryAction.CONFIGURE_MFA,
  [AuthErrorCode.BACKUP_CODE_INVALID]: AuthRecoveryAction.RETRY,

  [AuthErrorCode.TOKEN_EXPIRED]: AuthRecoveryAction.LOGOUT,
  [AuthErrorCode.TOKEN_INVALID]: AuthRecoveryAction.LOGOUT,
  [AuthErrorCode.SESSION_EXPIRED]: AuthRecoveryAction.LOGOUT,
  [AuthErrorCode.REFRESH_TOKEN_EXPIRED]: AuthRecoveryAction.LOGOUT,

  [AuthErrorCode.RATE_LIMIT_EXCEEDED]: AuthRecoveryAction.WAIT_AND_RETRY,
  [AuthErrorCode.TOO_MANY_ATTEMPTS]: AuthRecoveryAction.WAIT_AND_RETRY,

  [AuthErrorCode.NETWORK_ERROR]: AuthRecoveryAction.CHECK_CONNECTION,
  [AuthErrorCode.SERVER_ERROR]: AuthRecoveryAction.RETRY,
  [AuthErrorCode.SERVICE_UNAVAILABLE]: AuthRecoveryAction.WAIT_AND_RETRY,
  [AuthErrorCode.TIMEOUT]: AuthRecoveryAction.CHECK_CONNECTION,

  [AuthErrorCode.VALIDATION_ERROR]: AuthRecoveryAction.RETRY,
  [AuthErrorCode.INVALID_INPUT]: AuthRecoveryAction.RETRY,

  [AuthErrorCode.RESET_TOKEN_EXPIRED]: AuthRecoveryAction.RESET_PASSWORD,
  [AuthErrorCode.RESET_TOKEN_INVALID]: AuthRecoveryAction.RESET_PASSWORD,
  [AuthErrorCode.PASSWORD_RECENTLY_USED]: AuthRecoveryAction.RETRY,

  [AuthErrorCode.UNKNOWN]: AuthRecoveryAction.RETRY,
  [AuthErrorCode.PERMISSION_DENIED]: AuthRecoveryAction.CONTACT_SUPPORT
};

/**
 * Mapping of error codes to severity levels
 */
export const AUTH_ERROR_SEVERITIES: Record<AuthErrorCode, AuthErrorSeverity> = {
  [AuthErrorCode.INVALID_CREDENTIALS]: AuthErrorSeverity.WARNING,
  [AuthErrorCode.INVALID_EMAIL]: AuthErrorSeverity.WARNING,
  [AuthErrorCode.INVALID_PASSWORD]: AuthErrorSeverity.WARNING,
  [AuthErrorCode.WEAK_PASSWORD]: AuthErrorSeverity.WARNING,

  [AuthErrorCode.ACCOUNT_LOCKED]: AuthErrorSeverity.ERROR,
  [AuthErrorCode.ACCOUNT_DISABLED]: AuthErrorSeverity.ERROR,
  [AuthErrorCode.ACCOUNT_NOT_FOUND]: AuthErrorSeverity.WARNING,
  [AuthErrorCode.ACCOUNT_NOT_VERIFIED]: AuthErrorSeverity.WARNING,
  [AuthErrorCode.EMAIL_ALREADY_EXISTS]: AuthErrorSeverity.WARNING,
  [AuthErrorCode.USERNAME_ALREADY_EXISTS]: AuthErrorSeverity.WARNING,

  [AuthErrorCode.MFA_REQUIRED]: AuthErrorSeverity.INFO,
  [AuthErrorCode.MFA_INVALID_CODE]: AuthErrorSeverity.WARNING,
  [AuthErrorCode.MFA_CODE_EXPIRED]: AuthErrorSeverity.WARNING,
  [AuthErrorCode.MFA_TOO_MANY_ATTEMPTS]: AuthErrorSeverity.ERROR,
  [AuthErrorCode.MFA_NOT_CONFIGURED]: AuthErrorSeverity.INFO,
  [AuthErrorCode.BACKUP_CODE_INVALID]: AuthErrorSeverity.WARNING,

  [AuthErrorCode.TOKEN_EXPIRED]: AuthErrorSeverity.WARNING,
  [AuthErrorCode.TOKEN_INVALID]: AuthErrorSeverity.WARNING,
  [AuthErrorCode.SESSION_EXPIRED]: AuthErrorSeverity.WARNING,
  [AuthErrorCode.REFRESH_TOKEN_EXPIRED]: AuthErrorSeverity.WARNING,

  [AuthErrorCode.RATE_LIMIT_EXCEEDED]: AuthErrorSeverity.WARNING,
  [AuthErrorCode.TOO_MANY_ATTEMPTS]: AuthErrorSeverity.WARNING,

  [AuthErrorCode.NETWORK_ERROR]: AuthErrorSeverity.ERROR,
  [AuthErrorCode.SERVER_ERROR]: AuthErrorSeverity.ERROR,
  [AuthErrorCode.SERVICE_UNAVAILABLE]: AuthErrorSeverity.ERROR,
  [AuthErrorCode.TIMEOUT]: AuthErrorSeverity.WARNING,

  [AuthErrorCode.VALIDATION_ERROR]: AuthErrorSeverity.WARNING,
  [AuthErrorCode.INVALID_INPUT]: AuthErrorSeverity.WARNING,

  [AuthErrorCode.RESET_TOKEN_EXPIRED]: AuthErrorSeverity.WARNING,
  [AuthErrorCode.RESET_TOKEN_INVALID]: AuthErrorSeverity.WARNING,
  [AuthErrorCode.PASSWORD_RECENTLY_USED]: AuthErrorSeverity.WARNING,

  [AuthErrorCode.UNKNOWN]: AuthErrorSeverity.ERROR,
  [AuthErrorCode.PERMISSION_DENIED]: AuthErrorSeverity.ERROR
};

/**
 * Minimum enforced wait times (security floor).
 * Prevents misconfiguration from weakening rate-limit / lockout protections.
 */
export const MIN_AUTH_WAIT_TIMES: Readonly<AuthErrorWaitTimes> = {
  rateLimitExceeded: 1000,       // 1 second minimum
  tooManyAttempts: 60000,        // 1 minute minimum
  accountLocked: 60000,          // 1 minute minimum
  mfaTooManyAttempts: 60000,     // 1 minute minimum
  passwordResetRedirect: 1000,   // 1 second minimum
  sessionTimeoutWarning: 10000   // 10 seconds minimum
};

/**
 * Injection token for providing AuthErrorConfig via Angular DI.
 *
 * Usage in app config:
 * ```ts
 * provideAppInitializer(() => {}), // or in providers array:
 * { provide: AUTH_ERROR_CONFIG, useValue: { waitTimes: { rateLimitExceeded: 10000 } } }
 * ```
 *
 * Partial values are merged with DEFAULT_AUTH_ERROR_CONFIG.
 * Wait times are clamped to MIN_AUTH_WAIT_TIMES to prevent security misconfig.
 */
export const AUTH_ERROR_CONFIG = new InjectionToken<Partial<AuthErrorConfig>>(
  'AUTH_ERROR_CONFIG'
);
